import {BaseView} from "@nolimitcity/slot-game/bin/core/base/BaseView";
import {TimelineLite} from "gsap";
import {IWinRatioConfig} from "@nolimitcity/slot-game/bin/core/winpresentation/WinRatio";
import {ScalableLayer} from "@nolimitcity/slot-game/bin/core/stage/ScalableLayer";
import {IResizeData, StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {TombOfAkhenatenGameConfig} from "../TombOfAkhenatenGameConfig";
import {CoinsFountain} from "../winpresentation/bigWin/CoinsFountain";
import {SoundManager} from "../sound/SoundManager";
import {TombOfAkhenatenParsedGameData} from "../server/data/TombOfAkhenatenParsedGameData";
import {TombOfAkhenatenParsedInitData} from "../server/data/TombOfAkhenatenParsedInitData";
import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {ServerEvent} from "@nolimitcity/slot-game/bin/core/server/event/ServerEvent";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";

export class WinTextView extends BaseView {
    private _tint: PIXI.Sprite;
    private _spineContainer: PIXI.Container;
    private _coins: PIXI.Sprite;
    private _layer: ScalableLayer;
    private _coinsFountain: CoinsFountain;
    private _gameData: TombOfAkhenatenParsedGameData | TombOfAkhenatenParsedInitData;
    private _isBonusGame: boolean = false;
    private _bigWinAnimations: string[] = [];

    constructor() {
        super();
        this._coinsFountain = new CoinsFountain();
    }

    public getStartAnimation(config: IWinRatioConfig): TimelineLite {
        this._tint.visible = false;
        const tl: TimelineLite = new TimelineLite();

        tl.add(() => {
            this.playBigWinAnimation(config, false);
            SoundManager.playBigWinLevelSound(this.getWinLevel(config));
        });
        tl.add(() => {
            this.playBigWinAnimation(config, true);
        }, 0.83);
        return tl;
    }

    public abort(config: IWinRatioConfig): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        if (config.text == undefined || config.text == "") {
            return tl;
        }
        return tl;
    }

    public end(config: IWinRatioConfig, displayDuration: number, fadeOutDuration: number): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        tl.add(() => {
            SoundManager.stopBigWinLevelSound(this.getWinLevel(config));
            SoundManager.playBigWinEndSound(this._isBonusGame);
        });
        tl.add(() => this.fadeAndRemove(fadeOutDuration), displayDuration);
        return tl;
    }

    protected addEventListeners(): void {
        EventHandler.addEventListener(this, ServerEvent.GAME_DATA_PARSED, (event: GameEvent) => this.onDataParsed(event.params[0]));
        EventHandler.addEventListener(this, ServerEvent.INIT_DATA_PARSED, (event: GameEvent) => this.onDataParsed(event.params[0]));
    }

    protected initAnimations(): void {
        this._layer = StageManager.getLayer(TombOfAkhenatenGameConfig.instance.LAYERS.BIG_WIN.name);

        this._tint = new PIXI.Sprite(GameResources.getTextures("big_win_tint")[0]);
        this._tint.position.set(360, 360);
        this._tint.anchor.set(0.5, 0.5);
        this._tint.scale.set(10, 10);
        this._tint.alpha = 0.5;

        this._spineContainer = new PIXI.Container();

        this._coins = new PIXI.Sprite(GameResources.getTextures("big_win_coins_1")[0]);
        this._coins.scale.set(2);
        this._coins.anchor.set(0.5, 0.5);
        this._coins.position.set(360, 825);

        this.setVisibility(false);
        this.addChild(this._tint, this._spineContainer, this._coins);
        this._layer.addChild(this);
    }

    protected onResize(data: IResizeData): void {
        super.onResize && super.onResize(data);
        let deviceOrientation = data.orientation;

        if (deviceOrientation === "landscape") {
            this._spineContainer.scale.set(1, 1);
            this._spineContainer.position.set(360, 360);
        } else {
            this._spineContainer.scale.set(0.85, 0.85);
            this._spineContainer.position.set(360, 280);
        }
    }

    private onDataParsed(data: TombOfAkhenatenParsedGameData | TombOfAkhenatenParsedInitData): void {
        this._gameData = data;
        this._isBonusGame = TombOfAkhenatenGameConfig.instance.BONUS_GAME_MODES.indexOf(data.mode) > -1;
    }

    private getWinLevel(config: IWinRatioConfig): number {
        this._tint.visible = true;
        let winType: string = config.type ? config.type as string : "bigWin",
            level: number = 0;

        switch (winType) {
            case "megaWin":
                level = 1;
                break;
            case "superMegaWin":
                level = 2;
                break;
            case "hugeMegaWin":
                level = 3;
                break;
        }
        return level;
    }

    private getSpineData(bigWinLevel: string): any {
        let currentWinLevel: string = bigWinLevel ? bigWinLevel : "",
            spineTexture: PIXI.spine.core.SkeletonData,
            skinName: string = "",
            animName: string = "";

        switch (currentWinLevel) {
            case "megaWin":
                spineTexture = GameResources.getSpineAsset("bigWin_2_start");
                skinName = "strongbull";
                animName = "strongbull";
                break;

            case "megaWinLoop":
                spineTexture = GameResources.getSpineAsset("bigWin_2_loop");
                skinName = "strongbull_loop";
                animName = "strongbull_loop";
                break;

            case "superMegaWin":
                spineTexture = GameResources.getSpineAsset("bigWin_3_start");
                skinName = "crowned";
                animName = "crowned";
                break;

            case "superMegaWinLoop":
                spineTexture = GameResources.getSpineAsset("bigWin_3_loop");
                skinName = "crowned";
                animName = "crowned_loop";
                break;

            case "hugeMegaWin":
                spineTexture = GameResources.getSpineAsset("bigWin_4_start");
                skinName = "greatkingship";
                animName = "greatkingship";
                break;

            case "hugeMegaWinLoop":
                spineTexture = GameResources.getSpineAsset("bigWin_4_loop");
                skinName = "greatkingship";
                animName = "greatkingship";
                break;

            case "bigWinLoop":
                spineTexture = GameResources.getSpineAsset("bigWin_1_loop");
                skinName = "beautiful_loop";
                animName = "beautiful_loop";
                break;

            case "bigWin":
            default:
                spineTexture = GameResources.getSpineAsset("bigWin_1_start");
                skinName = "beautiful";
                animName = "beautiful";
                break;
        }

        return {
            "textureData": spineTexture,
            "skinName": skinName,
            "animName": animName
        };
    }

    public playBigWinAnimation(config: IWinRatioConfig, loop: boolean): TimelineLite {
        this._coinsFountain.startFountain(this.getWinLevel(config));
        let strSuffix: string = loop ? "Loop" : "",
            spineData: any = this.getSpineData((config.type + strSuffix) as string),
            spineTexture: PIXI.spine.core.SkeletonData = spineData.textureData,
            skinName: string = spineData.skinName,
            animName: string = spineData.animName,
            tl: TimelineLite = new TimelineLite();

        tl.add(() => {
            this.setVisibility(true);
            this.hideAllBigWinAnimations();
            let spineName: string = config.type + strSuffix;

            if(this._bigWinAnimations.indexOf(config.type + strSuffix) < 0){
                let spine: PIXI.spine.Spine = new PIXI.spine.Spine(spineTexture);
                spine.position.set(0, 0);
                spine.name = spineName;
                spine.skeleton.setSkin(null);
                spine.skeleton.setSkinByName(skinName);
                spine.skeleton.setSlotsToSetupPose();

                this._spineContainer.addChild(spine);
                this._bigWinAnimations.push(spine.name);
                spine.state.setAnimation(0, animName, loop);
            }else{
                let existingSpine: PIXI.spine.Spine = this._spineContainer.getChildByName(spineName) as PIXI.spine.Spine;
                existingSpine.updateTransform();
                existingSpine.visible = true;
                existingSpine.state.setAnimation(0, animName, loop);
            }
        });
        tl.add(() => {
            this.updateText(config);
        });
        return tl;
    }

    private updateText(config: IWinRatioConfig) {
        let coinsTexture: string = "big_win_coins_1";

        switch (config.text) {
            case "Mega Win":
                coinsTexture = "big_win_coins_2";
                break;
            case "Super Mega Win":
                coinsTexture = "big_win_coins_3";
                break;
            case "Huge Mega Win":
                coinsTexture = "big_win_coins_4";
                break;
        }
        this._coins.texture = GameResources.getTextures(coinsTexture)[0];
    }

    private fadeAndRemove(duration: number) {
        const tl: TimelineLite = new TimelineLite();

        tl.add(() => {
            this._coinsFountain.stopFountain();
            this.hideAllBigWinAnimations();
            this._coins.visible = false;
            this._tint.visible = false;
        }, duration);
        return tl;
    }

    private setVisibility(flag: boolean): void {
        this._tint.visible = flag;
        this._spineContainer.visible = flag;
        this._coins.visible = flag;
    }

    private hideAllBigWinAnimations(): void{
        (<PIXI.spine.Spine[]>this._spineContainer.children).forEach((spine: PIXI.spine.Spine) => {
            spine.state.clearTracks();
            spine.visible = false;
        });
    }
}